import { useState, useTransition } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const languages = [
	{ code: "en", label: "English", flag: "🇺🇸" },
	{ code: "ru", label: "Русский", flag: "🇷🇺" },
] as const;

export function LanguageSwitcher({ collapsed = false }: { collapsed?: boolean }) {
	const [isOpen, setIsOpen] = useState(false);
	const [isPending, startTransition] = useTransition();
	const { i18n } = useTranslation();

	const handleLocaleChange = (locale: string) => {
		startTransition(() => {
			i18n.changeLanguage(locale);
		});
		setIsOpen(false);
	};

	const currentLocale = i18n.language;
	const currentLanguage =
		languages.find((l) => l.code === currentLocale) || languages[0];

	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				disabled={isPending}
				aria-label={currentLanguage.label}
				title={collapsed ? currentLanguage.label : undefined}
				aria-expanded={isOpen}
				className={cn(
					"flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm font-medium",
					"text-text-secondary transition-colors",
					"hover:bg-bg-canvas hover:text-text-primary group",
					"disabled:cursor-not-allowed disabled:text-action-disabled-fg",
					collapsed && "justify-center px-0",
				)}
			>
				<div className="flex items-center gap-3">
					<Globe size={18} className="shrink-0 text-text-secondary group-hover:text-text-primary" />
					<span className={collapsed ? "sr-only" : undefined}>
						{currentLanguage.flag} {currentLanguage.label}
					</span>
				</div>
				{!collapsed && (
					<ChevronDown
						size={14}
						aria-hidden="true"
						className={cn(
							"text-text-tertiary transition-transform duration-150",
							isOpen && "rotate-180",
						)}
					/>
				)}
			</button>

			{isOpen && (
				<>
					<div
						className="fixed inset-0 z-40"
						onClick={() => setIsOpen(false)}
					/>
					<div
						className={cn(
							"absolute bottom-full left-0 z-50 mb-1 rounded-xl border border-border-subtle bg-bg-surface py-1 shadow-popover",
							collapsed ? "w-40" : "w-full",
						)}
					>
						{languages.map((lang) => (
							<button
								key={lang.code}
								type="button"
								onClick={() => handleLocaleChange(lang.code)}
								className={cn(
									"flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
									"transition-colors hover:bg-bg-sunken",
									lang.code === currentLocale
										? "bg-brand-100 text-text-primary font-medium"
										: "text-text-secondary hover:text-text-primary",
								)}
							>
								<span>{lang.flag}</span>
								<span>{lang.label}</span>
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
}
